/**
 * "Create reason" from a reason picker (FB-1013): the picker's search becomes the
 * title, and the rest of the reason (description, legal reference) is filled in
 * here, with the same fields and limits as Manage → Redaction Reasons. Saving
 * adds the reason to every reasons list (createSharedReason) and hands it back so
 * the picker can select it.
 */

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { hasReasonTitle } from "../editor/reasonOptions";
import type { RedactionReason } from "../../types";
import { Button } from "../ui/Button";
import { CharCount } from "../ui/CharCount";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  CITATION_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
  LEGAL_TEXT_MAX_LENGTH,
  TITLE_MAX_LENGTH,
  URL_MAX_LENGTH,
  isValidReasonUrl,
} from "../../utils/reasonLegal";
import { emptyReasonDraft, reasonDraftProblem, saveReasonDraft, type ReasonDraft } from "./reasonDraft";
import { UrlError } from "./UrlError";

const INPUT_CLASS =
  "w-full px-3 py-2 border border-border rounded-md bg-white text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:border-primary transition-colors";
const LABEL_CLASS = "block text-xs font-medium text-gray-500 mb-1";

interface CreateReasonFormProps {
  draft: ReasonDraft;
  onChange: (draft: ReasonDraft) => void;
  /** The reasons that exist, to flag a duplicate title. */
  reasons: readonly Pick<RedactionReason, "title">[];
  submitting: boolean;
  /** The API's error from the last save, if any. */
  error: string | null;
}

/** The fields (no dialog chrome), so it can be rendered on its own. */
export function CreateReasonForm({ draft, onChange, reasons, submitting, error }: CreateReasonFormProps) {
  const set = (field: keyof ReasonDraft) => (e: { target: { value: string } }) =>
    onChange({ ...draft, [field]: e.target.value });
  const duplicate = !!draft.title.trim() && hasReasonTitle(reasons, draft.title);

  return (
    <div className="space-y-3">
      <div>
        <label className={LABEL_CLASS} htmlFor="create-reason-title">
          Title <span className="font-normal text-gray-400">(shown in the redacted document)</span>
        </label>
        <input
          id="create-reason-title"
          type="text"
          value={draft.title}
          onChange={set("title")}
          maxLength={TITLE_MAX_LENGTH}
          className={INPUT_CLASS}
          disabled={submitting}
          autoFocus
          aria-invalid={duplicate}
        />
        <CharCount value={draft.title} max={TITLE_MAX_LENGTH} />
        {duplicate && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            A reason with this title already exists. Pick it from the list.
          </p>
        )}
      </div>

      <div>
        <label className={LABEL_CLASS} htmlFor="create-reason-description">
          Description <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <input
          id="create-reason-description"
          type="text"
          value={draft.description}
          onChange={set("description")}
          maxLength={DESCRIPTION_MAX_LENGTH}
          className={INPUT_CLASS}
          disabled={submitting}
        />
        <CharCount value={draft.description} max={DESCRIPTION_MAX_LENGTH} />
      </div>

      <div className="pt-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Legal reference{" "}
          <span className="normal-case tracking-normal font-normal text-gray-400">
            (optional, shown to the public in the redaction log)
          </span>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start mb-3">
          <div>
            <label className={LABEL_CLASS} htmlFor="create-reason-citation">
              Citation
            </label>
            <input
              id="create-reason-citation"
              type="text"
              value={draft.citation}
              onChange={set("citation")}
              placeholder="e.g., RCW 42.56.230(1)"
              maxLength={CITATION_MAX_LENGTH}
              className={INPUT_CLASS}
              disabled={submitting}
            />
            <CharCount value={draft.citation} max={CITATION_MAX_LENGTH} />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="create-reason-url">
              Link to the statute
            </label>
            <input
              id="create-reason-url"
              type="url"
              value={draft.url}
              onChange={set("url")}
              placeholder="https://..."
              aria-invalid={!isValidReasonUrl(draft.url)}
              maxLength={URL_MAX_LENGTH}
              className={INPUT_CLASS}
              disabled={submitting}
            />
            <UrlError valid={isValidReasonUrl(draft.url)} />
          </div>
        </div>
        <label className={LABEL_CLASS} htmlFor="create-reason-legal-text">
          Full legal text
        </label>
        <textarea
          id="create-reason-legal-text"
          value={draft.legal_text}
          onChange={set("legal_text")}
          placeholder="Full statutory text or explanation shown in the redaction log"
          rows={3}
          maxLength={LEGAL_TEXT_MAX_LENGTH}
          className={`${INPUT_CLASS} resize-y`}
          disabled={submitting}
        />
        <CharCount value={draft.legal_text} max={LEGAL_TEXT_MAX_LENGTH} />
      </div>

      {error && (
        <div
          role="alert"
          className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600 break-words whitespace-pre-wrap"
        >
          {error}
        </div>
      )}
    </div>
  );
}

interface CreateReasonDialogProps {
  open: boolean;
  /** Prefills the title (the picker's search). */
  initialTitle: string;
  reasons: readonly Pick<RedactionReason, "title">[];
  onCreated: (reason: RedactionReason) => void;
  /** Escape, Cancel, the close button or the backdrop. */
  onCancel: () => void;
}

export function CreateReasonDialog({ open, initialTitle, reasons, onCreated, onCancel }: CreateReasonDialogProps) {
  const [draft, setDraft] = useState<ReasonDraft>(() => emptyReasonDraft(initialTitle));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(emptyReasonDraft(initialTitle));
    setError(null);
    setSaved(false);
  }, [open, initialTitle]);

  const problem = reasonDraftProblem(draft, reasons);

  const submit = async () => {
    if (problem || submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await saveReasonDraft(draft);
    setSubmitting(false);
    if (result.reason) {
      setSaved(true);
      onCreated(result.reason);
    } else {
      setError(result.error);
    }
  };

  // The dialog renders in a portal, but React still bubbles its events through
  // the picker's ancestors: keep clicks off the card underneath (they navigate
  // or select), and keys off the editor's shortcuts and surrounding popups (the
  // manual-redaction picker confirms on Enter). Escape is stopped earlier, below.
  // Pointer-downs still bubble: Radix needs them to close on a backdrop click.
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

  return (
    <div className="contents" onClick={stop} onKeyDown={stop}>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && !submitting) onCancel();
        }}
      >
        <DialogContent
          // Opens from inside other popups (the manual-redaction picker sits at
          // z-index 9999), so it has to stack above them.
          className="z-[10000] sm:max-w-[560px] bg-white"
          overlayClassName="z-[10000]"
          data-testid="create-reason-dialog"
          // Radix sees Escape on the way down (document, capture) and closes the
          // form, which unmounts it before the key bubbles back up: stop it here so
          // the popup or card holding the picker doesn't take it as its own cancel.
          onEscapeKeyDown={(e) => e.stopPropagation()}
          // After Cancel the picker reopens and focuses its search; after Save
          // focus goes back to the picker's button, now showing the new reason.
          onCloseAutoFocus={(e) => {
            if (!saved) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Create reason</DialogTitle>
            <DialogDescription>
              Added to the reasons list for everyone in your organization and selected here.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <CreateReasonForm draft={draft} onChange={setDraft} reasons={reasons} submitting={submitting} error={error} />
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!!problem || submitting}
                className="bg-gray-900 text-white hover:bg-gray-800"
                data-testid="create-reason-save"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Create and select
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
